import { Numberish } from 'lib/enums/utilities';
import _ from 'lodash';
import { createContext, FC, ReactNode, useMemo, useState } from 'react';

type ToggleErrorFn = (fieldName: string) => void;

interface State {
  lastSubmitted: Numberish;

  data: any;
  errors: string[];
}

interface Actions {
  readonly onSubmit: () => void;

  readonly setData: (v: any, replace?: boolean) => void;

  readonly addError: ToggleErrorFn;
  readonly removeError: ToggleErrorFn;
}

interface Context extends State, Actions {}

const DEFAULT: Context = {
  lastSubmitted: undefined,
  onSubmit: () => console.log('FormContext not init'),

  data: undefined,
  setData: () => console.log('FormContext not init'),

  errors: [],
  addError: () => console.log('FormContext not init'),
  removeError: () => console.log('FormContext not init'),
};

// for simplifying cases where we need to juggle data around
// e.g. a form is in a dialog, dialog close => submit form data
export const FormContext = createContext(DEFAULT);

interface Props {
  // will be deep-cloned to avoid modifying the original
  defaultData: any;
  children: ReactNode;
}

export const FormProvider: FC<Props> = (props) => {
  const [lastSubmitted, setLastSubmitted] = useState(DEFAULT.lastSubmitted);

  const [data, setData] = useState(_.cloneDeep(props.defaultData));

  const [errorDict, setErrorDict] = useState<Set<string>>(new Set());

  const errors = useMemo(() => Array.from(errorDict), [errorDict]);

  return (
    <FormContext.Provider
      value={{
        lastSubmitted: lastSubmitted,
        onSubmit: () => setLastSubmitted(Date.now()),

        data: data,
        setData: (v, replace) =>
          setData((prev: any) => (replace ? v : { ...prev, ...v })),

        errors: errors,

        addError: (field) =>
          setErrorDict((prev) => {
            prev.add(field);
            return _.clone(prev);
          }),

        removeError: (field) =>
          setErrorDict((prev) => {
            prev.delete(field);
            return _.clone(prev);
          }),
      }}
    >
      {props.children}
    </FormContext.Provider>
  );
};
